import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Instagram, Facebook, Youtube, Menu, X, Phone, ShoppingBag, User } from "lucide-react";
import { useSettings } from "../lib/SettingsContext";
import { useCart } from "../lib/CartContext";
import { useCustomer } from "../lib/CustomerContext";
import Logo from "./Logo";
import TikTokIcon from "./TikTokIcon";

export default function Navbar() {
    const { settings } = useSettings();
    const { itemCount } = useCart();
    const { customer } = useCustomer();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const links = [
        { to: "/", label: "Home" },
        { to: "/about", label: "About Us" },
        { to: "/flavours", label: "Flavours" },
        { to: "/gallery", label: "Gallery" },
        { to: "/shop", label: "Shop" },
        { to: "/blog", label: "Blog" },
        { to: "/contact", label: "Contact" },
    ];

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
                scrolled ? "bg-white/95 backdrop-blur-sm shadow-[0_1px_0_rgba(236,0,140,0.08)]" : "bg-[#FFF7FB]"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Row 1: hamburger (mobile) · logo (center) · social + phone */}
                <div className="grid grid-cols-3 items-center h-16 md:h-24">
                    {/* Left — mobile hamburger / desktop empty */}
                    <div className="flex items-center justify-start">
                        <button
                            className="md:hidden text-ink p-2 -ml-2"
                            onClick={() => setOpen((v) => !v)}
                            aria-label="Toggle menu"
                            data-testid="nav-toggle"
                        >
                            {open ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>

                    {/* Center — Logo */}
                    <Link
                        to="/"
                        className="flex items-center justify-center"
                        data-testid="nav-logo"
                    >
                        <Logo variant="dark" className="h-10 md:h-16" />
                    </Link>

                    {/* Right — social + phone */}
                    <div className="flex items-center justify-end gap-3 md:gap-5">
                        <div className="hidden md:flex items-center gap-4">
                            {settings.instagram_url && (
                                <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="text-blush hover:text-[#C4007A] transition-colors" data-testid="social-instagram">
                                    <Instagram size={17} />
                                </a>
                            )}
                            {settings.facebook_url && (
                                <a href={settings.facebook_url} target="_blank" rel="noreferrer" className="text-blush hover:text-[#C4007A] transition-colors" data-testid="social-facebook">
                                    <Facebook size={17} />
                                </a>
                            )}
                            {settings.tiktok_url && (
                                <a href={settings.tiktok_url} target="_blank" rel="noreferrer" className="text-blush hover:text-[#C4007A] transition-colors" data-testid="social-tiktok">
                                    <TikTokIcon size={17} />
                                </a>
                            )}
                            {settings.youtube_url && (
                                <a href={settings.youtube_url} target="_blank" rel="noreferrer" className="text-blush hover:text-[#C4007A] transition-colors" data-testid="social-youtube">
                                    <Youtube size={17} />
                                </a>
                            )}
                        </div>
                        <a
                            href={`tel:${settings.phone?.replace(/\s/g, "")}`}
                            className="hidden md:inline-flex items-center gap-2 text-sm tracking-wider text-ink hover:text-blush transition-colors"
                            data-testid="nav-phone"
                        >
                            <Phone size={14} className="text-blush" />
                            <span>{settings.phone}</span>
                        </a>
                        <a
                            href={`tel:${settings.phone?.replace(/\s/g, "")}`}
                            className="md:hidden text-ink hover:text-blush transition-colors"
                            aria-label="Call us"
                        >
                            <Phone size={18} className="text-blush" />
                        </a>
                        {/* Account — logged in shows /account, guests get /login */}
                        <Link
                            to={customer ? "/account" : "/login"}
                            className="text-ink hover:text-blush transition-colors"
                            aria-label={customer ? "My account" : "Log in"}
                            data-testid="nav-account"
                            title={customer ? `Hi ${customer.name.split(" ")[0]}` : "Log in / sign up"}
                        >
                            <User size={18} />
                        </Link>
                        {/* Cart */}
                        <Link
                            to="/cart"
                            className="relative text-ink hover:text-blush transition-colors"
                            aria-label="Cart"
                            data-testid="nav-cart"
                        >
                            <ShoppingBag size={18} />
                            {itemCount > 0 && (
                                <span className="absolute -top-2 -right-2 bg-blush text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                                    {itemCount}
                                </span>
                            )}
                        </Link>
                    </div>
                </div>

                {/* Row 2: nav links centered (desktop only) */}
                <nav className="hidden md:flex items-center justify-center gap-6 lg:gap-12 py-3 border-t border-blush/15">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            className={({ isActive }) =>
                                `nav-link text-[11px] tracking-[0.28em] uppercase font-medium ${
                                    isActive ? "active text-blush" : "text-ink hover:text-blush"
                                }`
                            }
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div className="md:hidden bg-white border-t border-blush/15" data-testid="mobile-menu">
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                className={({ isActive }) =>
                                    `text-sm tracking-[0.22em] uppercase ${isActive ? "text-blush" : "text-ink"}`
                                }
                                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <div className="flex items-center gap-5 pt-4 border-t border-blush/15">
                            {settings.instagram_url && <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="text-blush hover:text-[#C4007A]"><Instagram size={18} /></a>}
                            {settings.facebook_url && <a href={settings.facebook_url} target="_blank" rel="noreferrer" className="text-blush hover:text-[#C4007A]"><Facebook size={18} /></a>}
                            {settings.tiktok_url && <a href={settings.tiktok_url} target="_blank" rel="noreferrer" className="text-blush hover:text-[#C4007A]"><TikTokIcon size={18} /></a>}
                        </div>
                        <a href={`tel:${settings.phone?.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 text-sm text-ink">
                            <Phone size={14} className="text-blush" /> {settings.phone}
                        </a>
                        <div className="pt-4 border-t border-blush/15">
                            {customer ? (
                                <Link to="/account" className="inline-flex items-center gap-2 text-sm tracking-[0.22em] uppercase text-blush">
                                    <User size={14} /> My account
                                </Link>
                            ) : (
                                <div className="flex gap-4 text-sm tracking-[0.22em] uppercase">
                                    <Link to="/login" className="text-ink hover:text-blush">Log in</Link>
                                    <span className="text-ink-soft">·</span>
                                    <Link to="/signup" className="text-blush">Sign up</Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
